import { Calendar, FileText, MessageSquare, Database } from "lucide-react";
import { Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface UseCase {
  icon: LucideIcon;
  title: string;
  description: string;
  example: string;
}

const useCases: UseCase[] = [
  {
    icon: Calendar,
    title: "Sprint Planning",
    description: "Review user stories before backlog grooming. Know exactly what engineers will ask.",
    example: '"Is this story ready for dev?"',
  },
  {
    icon: FileText,
    title: "PRD Handoff",
    description: "Ensure clarity before development sees it. Prevent the back-and-forth.",
    example: '"What assumptions am I missing?"',
  },
  {
    icon: MessageSquare,
    title: "Stakeholder Requests",
    description: "Translate vague asks into actionable specs. Bridge the communication gap.",
    example: '"Make it more user-friendly"',
  },
  {
    icon: Database,
    title: "Migration Projects",
    description: "Document legacy systems clearly. Capture tribal knowledge before it's lost.",
    example: '"How does this actually work?"',
  },
];

export function UseCaseCards() {
  return (
    <section className="py-14 md:py-20 relative overflow-hidden">
      <div className="absolute bottom-[20%] right-[10%] w-[400px] h-[400px] rounded-full bg-primary/[0.03] blur-[140px]" />

      <div className="container relative z-10 px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Use Cases
          </div>
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-3">
            Built for your workflow
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Reqqy fits into the moments where clarity matters most
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {useCases.map((useCase, index) => (
            <div
              key={useCase.title}
              className="group relative p-5 rounded-xl glass border border-border/30 animate-fade-in transition-all duration-300 hover:border-primary/20 hover:-translate-y-0.5"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-primary/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <useCase.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-sm font-semibold mb-1.5">{useCase.title}</h3>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{useCase.description}</p>
                <div className="text-[11px] text-muted-foreground/60 italic border-t border-border/30 pt-2.5">
                  {useCase.example}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
